"use client";

import { useEffect, useState } from "react";

export interface ChatQuota {
  used: number;
  cap: number;
}

/**
 * Today's chat allowance, fetched once per mount from `/api/chat/quota`.
 *
 * Extracted because the identical fetch/validate/guard block existed in two
 * components (ChatClient and the account dropdown's quota line), each
 * enforcing the response contract by hand — and both fail SILENTLY by design
 * (no quota → no ring), so a drift between the copies would just make one
 * surface quietly disappear forever.
 *
 * Returns the setter too: the chat page updates the number from every turn
 * response (`chat_begin_turn` returns its counters), which is fresher than
 * any refetch.
 */
export function useChatQuota(enabled = true): [ChatQuota | null, (q: ChatQuota) => void] {
  const [quota, setQuota] = useState<ChatQuota | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    fetch("/api/chat/quota")
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { ok?: boolean; used?: number; cap?: number } | null) => {
        if (live && j?.ok && typeof j.used === "number" && typeof j.cap === "number") {
          setQuota({ used: j.used, cap: j.cap });
        }
      })
      .catch(() => {});
    return () => { live = false; };
  }, [enabled]);
  return [quota, setQuota];
}
